// ** User Profile Store **
//
// Sets and gets profile data.

import data_id_content from '../content/general/choice-group-items.json'

import {
  getTenantConfig as getTenantConfigFromIndex,
  getTenantTokens as getTenantTokensFromIndex,
  getTenantCampuses as getTenantCampusesFromIndex,
  hasCampusPicker as hasCampusPickerFromIndex,
  normaliseTenantKey,
} from '../tenants'

const root = {
  root: true,
}

const getDefaultState = () => {
  return {
    positivesList: null,
    negativesList: null,

    // ─────────────────────────────────────────────────────────────
    // Tenant / localisation state
    // ─────────────────────────────────────────────────────────────
    tenant_key: 'ucc',
    campus_key: null,

    // Set as non-user by default
    dast_score: 0,

    dastList: [],
    quiz_score: null,
    user_type: null,
    primary_drug: null,
    personalGoals: [],
    injunctiveNorms: [],
    descriptiveNorms: [],
    relationship_scores: [],
    behavioural_score: null,
    willingness_score: null,
    confidence_score: null,
    life_values_score: null,
    decision_making_score: null,
    smart: [],
    influences: null,
    needs: null,
    skills: null,
    hooks: null,
    helps: null,
    values: null,
    values_scores: null,
    reasons_for_use: null,
    hr_practices: null,
    hr_commitment: null,
    other_influences: null,
  }
}

// Initial state
export const state = getDefaultState()

export const mutations = {
  RESET_STATE(state) {
    const currentTenant = state.tenant_key
    const currentCampus = state.campus_key

    Object.assign(state, getDefaultState())

    // Keep tenant/campus across profile resets.
    state.tenant_key = currentTenant || 'ucc'
    state.campus_key = currentCampus || null
  },

  setTenantKey(state, tenantKey) {
    const key = normaliseTenantKey(tenantKey)
    state.tenant_key = key

    if (process.client) {
      try {
        localStorage.setItem('tenant', key)
      } catch (_) {}
    }
  },

  setCampusKey(state, campusKey) {
    const value =
      campusKey == null || campusKey === ''
        ? null
        : String(campusKey)
            .trim()
            .toLowerCase()
  
    state.campus_key = value
  
    if (process.client) {
      try {
        const tenantKey =
          normaliseTenantKey(
            state.tenant_key || 'ucc'
          )
  
        const storageKey =
          `campus:${tenantKey}`
  
        if (value) {
          localStorage.setItem(
            storageKey,
            value
          )
        } else {
          localStorage.removeItem(
            storageKey
          )
        }
  
        /*
         * Remove the old generic campus key so
         * previous versions cannot silently
         * preselect a campus.
         */
        localStorage.removeItem('campus')
      } catch (_) {}
    }
  },

  setTenantSelection(state, payload = {}) {
    const tenantKey =
      normaliseTenantKey(
        payload.tenant ||
        payload.tenant_key
      )
  
    const campusKey =
      payload.campus ||
      payload.campus_key ||
      payload.selectedCampusKey ||
      null
  
    state.tenant_key = tenantKey
  
    state.campus_key =
      campusKey == null ||
      campusKey === ''
        ? null
        : String(campusKey)
            .trim()
            .toLowerCase()
  
    if (process.client) {
      try {
        localStorage.setItem(
          'tenant',
          tenantKey
        )
  
        const storageKey =
          `campus:${tenantKey}`
  
        if (state.campus_key) {
          localStorage.setItem(
            storageKey,
            state.campus_key
          )
        } else {
          localStorage.removeItem(
            storageKey
          )
        }
  
        /*
         * Clean up the old generic campus key.
         */
        localStorage.removeItem('campus')
      } catch (_) {}
    }
  },

  setDASTAnswers(state, dastList) {
    state.dastList = dastList
  },

  setDASTScore(state, score) {
    state.dast_score = parseInt(score)
  },

  // Used as part of the non-user path
  async setUserType(state, type) {
    state.user_type = parseInt(type)

    await this.$axios.$post(
      this.$config.BASE_API +
        '/activity/commit_user_type',
      {
        user_type: state.user_type,
      }
    )
  },

  // 1-2 user only:
  // Drugs which the user has taken.
  //
  // We send the entire string here because it is matched server-side.
  async setSelectedDrugList(state, selected) {
    // Do not store again while renavigating
    if (
      !state.primary_drug &&
      selected.primary_drug
    ) {
      await this.$axios.$post(
        this.$config.BASE_API +
          '/activity/commit_selected_drugs',
        {
          selection: selected.selected_drugs
            .map((item) => item.backend_id)
            .toString(),

          primary_drug:
            selected.primary_drug,
        }
      )
    }

    this.commit(
      'profile/setPrimaryDrug',
      selected.primary_drug
    )
  },

  // 1-6: positives and negatives together
  setPosNegs(state, posnegs) {
    // Never mutate profile while reviewing
    if (
      this.state?.navigation?.review_mode
    ) {
      return
    }

    // Ignore empty or invalid payloads
    if (
      !Array.isArray(posnegs) ||
      posnegs.length === 0
    ) {
      return
    }

    const negatives = posnegs.filter(
      (object) =>
        object?.data_id?.includes(
          'negative'
        )
    )

    const positives = posnegs.filter(
      (object) =>
        object?.data_id?.includes(
          'positive'
        )
    )

    // If both lists are empty, treat as a no-op
    if (
      negatives.length === 0 &&
      positives.length === 0
    ) {
      return
    }

    // Persist once and store locally
    try {
      helpers.storeActivityOnServer(
        positives,
        0,
        this.$axios,
        this.$config
      )

      helpers.storeActivityOnServer(
        negatives,
        3,
        this.$axios,
        this.$config
      )
    } catch (_) {}

    negatives.stored = true
    positives.stored = true

    state.negativesList = negatives
    state.positivesList = positives
  },

  // 1-6: explicit single-list updates
  setPositives(state, positives) {
    if (
      this.state?.navigation?.review_mode
    ) {
      return
    }

    if (
      !Array.isArray(positives) ||
      positives.length === 0
    ) {
      return
    }

    positives.stored = true
    state.positivesList = positives
  },

  setNegatives(state, negatives) {
    if (
      this.state?.navigation?.review_mode
    ) {
      return
    }

    if (
      !Array.isArray(negatives) ||
      negatives.length === 0
    ) {
      return
    }

    negatives.stored = true
    state.negativesList = negatives
  },

  // 1-6 user only: primary drug choice
  setPrimaryDrug(state, primary_drug) {
    state.primary_drug = primary_drug
  },

  // 1-3 non-user only: quiz score
  setQuizScore(state, quiz_score) {
    if (!state.quiz_score) {
      helpers.storeScoreOnServer(
        quiz_score,
        2,
        this.$axios,
        this.$config
      )
    }

    state.quiz_score =
      parseInt(quiz_score)
  },

  // 1-11
  setPersonalGoals(state, value) {
    // Ensure the array is the same format returned from filter
    try {
      value = value.filter(
        function (element) {
          return (
            element != null &&
            !Array.isArray(element) &&
            element.length > 0
          )
        }
      )
    } catch (error) {
      value = [value]
    }

    this.$axios.$post(
      this.$config.BASE_API +
        '/activity/commit_activity',
      {
        value: JSON.stringify(value),
        type: 2,
      }
    )

    state.personalGoals = value
  },

  // 1-12
  setDescriptiveNorms(
    state,
    descriptive_norms
  ) {
    state.descriptiveNorms =
      helpers.clone(
        descriptive_norms
      )
  },

  // 1-13
  setInjunctiveNorms(
    state,
    injunctive_norms
  ) {
    if (!state.injunctiveNorms) {
      helpers.storeActivityOnServer(
        injunctive_norms.toString(),
        6,
        this.$axios,
        this.$config
      )
    }

    // Mark as stored
    injunctive_norms.stored = true

    state.injunctiveNorms =
      injunctive_norms
  },

  // 2-1
  setRelationshipScore(
    state,
    scores
  ) {
    if (!state.relationship_scores) {
      helpers.storeScoreOnServer(
        scores.toString(),
        6,
        this.$axios,
        this.$config
      )
    }

    state.relationship_scores = scores
  },

  // 2-5
  setBehaviouralAwarenessScore(
    state,
    score
  ) {
    if (!state.behavioural_score) {
      helpers.storeScoreOnServer(
        score,
        5,
        this.$axios,
        this.$config
      )
    }

    state.behavioural_score = score
  },

  // 2-7
  setDecisionMakingScore(
    state,
    score
  ) {
    if (!state.decision_making_score) {
      helpers.storeScoreOnServer(
        score,
        3,
        this.$axios,
        this.$config
      )
    }

    state.decision_making_score =
      score
  },

  // 1-10C
  setConfidenceToChangeScore(
    state,
    confidence_score
  ) {
    if (!state.confidence_score) {
      helpers.storeScoreOnServer(
        confidence_score,
        7,
        this.$axios,
        this.$config
      )
    }

    state.confidence_score =
      helpers.clone(
        confidence_score
      )
  },

  // Capture willingness to change locally
  setWillingnessToChangeScore(
    state,
    willingness_score
  ) {
    state.willingness_score =
      parseInt(willingness_score)
  },

  // 4A-3 and 4-6
  setInfluences(state, influences) {
    if (!state.influences) {
      helpers.storeActivityOnServer(
        influences,
        5,
        this.$axios,
        this.$config
      )
    }

    state.influences = influences
  },

  // 2-4
  // Scoring function takes place in the component
  setLifeValuesScore(state, score) {
    if (!state.life_values_score) {
      helpers.storeScoreOnServer(
        score,
        4,
        this.$axios,
        this.$config
      )
    }

    state.life_values_score = score
  },

  // 3-2
  // Save an array of chosen strings
  setValues(state, values) {
    helpers.storeBlobActivityOnServer(
      values,
      9,
      this.$axios,
      this.$config
    )

    state.values = values
  },

  // 3-3: rate your values
  setValueScores(
    state,
    values_scores
  ) {
    helpers.storeBlobActivityOnServer(
      values_scores,
      17,
      this.$axios,
      this.$config
    )

    state.values_scores =
      values_scores
  },

  // 2C-2
  // Stored within the navigation string only
  setNeeds(state, needs) {
    if (!state.needs) {
      helpers.storeActivityOnServer(
        needs,
        11,
        this.$axios,
        this.$config
      )
    }

    state.needs = needs
  },

  // 2C-1
  // Stored within the navigation string only
  setHelps(state, helps) {
    if (!state.helps) {
      helpers.storeActivityOnServer(
        helps,
        12,
        this.$axios,
        this.$config
      )
    }

    state.helps = helps
  },

  // 5-1: HR practices
  setHRPractices(
    state,
    hr_practices
  ) {
    if (
      this.state?.navigation?.review_mode
    ) {
      return
    }

    if (
      !Array.isArray(hr_practices) ||
      hr_practices.length === 0
    ) {
      return
    }

    // Only store once on the server,
    // but always keep local state fresh.
    if (!state.hr_practices) {
      helpers.storeActivityOnServer(
        hr_practices,
        12,
        this.$axios,
        this.$config
      )
    }

    state.hr_practices =
      hr_practices
  },

  // 5-3: HR commitment
  setHRCommitment(
    state,
    hr_commitment
  ) {
    if (
      this.state?.navigation?.review_mode
    ) {
      return
    }

    const value =
      Number(hr_commitment)

    if (!Number.isFinite(value)) {
      return
    }

    if (
      state.hr_commitment == null
    ) {
      helpers.storeScoreOnServer(
        value,
        8,
        this.$axios,
        this.$config
      )
    }

    state.hr_commitment = value
  },

  // 5A-3
  setSkills(state, skills) {
    if (!state.skills) {
      helpers.storeActivityOnServer(
        skills,
        15,
        this.$axios,
        this.$config
      )
    }

    state.skills = skills
  },

  // 4-2
  setReasonsForUse(
    state,
    reasons_for_use
  ) {
    if (!state.reasons_for_use) {
      helpers.storeActivityOnServer(
        reasons_for_use,
        10,
        this.$axios,
        this.$config
      )
    }

    state.reasons_for_use =
      reasons_for_use
  },

  // 4-6
  setOtherInfluences(
    state,
    other_influences
  ) {
    if (!state.other_influences) {
      // Special endpoint for free text
      helpers.storeStringOnServer(
        other_influences,
        16,
        this.$axios,
        this.$config
      )
    }

    state.other_influences =
      other_influences
  },

  // 4-5
  setDarraghHooks(state, hooks) {
    if (!state.hooks) {
      helpers.storeActivityOnServer(
        hooks,
        7,
        this.$axios,
        this.$config
      )
    }

    state.hooks = hooks
  },

  // 3-7
  setSMART(state, smart) {
    if (
      (
        Array.isArray(state.smart) &&
        state.smart.length === 0
      ) ||
      (
        typeof state.smart ===
          'string' &&
        state.smart.length === 2
      )
    ) {
      this.$axios.$post(
        this.$config.BASE_API +
          '/activity/commit_activity',
        {
          value: JSON.stringify(smart),
          type: 4,
        }
      )
    }

    state.smart =
      helpers.clone(smart)
  },
}

export const actions = {
  // Reset state when returning to the main page
  resetState({ commit }) {
    commit('RESET_STATE')
  },

  initialiseTenantSelection({ commit }) {
    const tenant = helpers.resolveTenantFromBrowser()
    const campus = helpers.resolveCampusFromBrowser()

    commit('setTenantSelection', {
      tenant,
      campus,
    })
  },

  setTenantKey({ commit }, tenantKey) {
    commit('setTenantKey', tenantKey)
  },

  setCampusKey({ commit }, campusKey) {
    commit('setCampusKey', campusKey)
  },

  setTenantSelection({ commit }, payload = {}) {
    commit('setTenantSelection', payload)
  },

  // Set the user type based on DAST10
  async setDAST10(
    { commit, dispatch },
    dastList
  ) {
    await this.$axios
      .$post(
        this.$config.BASE_API +
          '/activity/commit_dast_responses',
        {
          dast_answers: dastList,
        }
      )
      .then((response) => {
        commit(
          'setUserType',
          response.user_type
        )

        commit(
          'setDASTScore',
          response.score
        )

        dispatch(
          'navigation/storeNavigationHistory',
          {},
          root
        )
      })

    commit(
      'setDASTAnswers',
      dastList
    )

    // Set page completed and allow continue
    await commit(
      'navigation/setPageCompleted',
      true,
      root
    )
  },

  // Recall a user's scores, activities and navigation history
  recallProfile(
    { commit, dispatch },
    profile
  ) {
    // 0) Keep tenant/campus state current when recalling a profile.
    // This does not save anything to backend; it only hydrates frontend localisation.
    try {
      commit('setTenantSelection', {
        tenant: helpers.resolveTenantFromBrowser(),
        campus: helpers.resolveCampusFromBrowser(),
      })
    } catch (_) {}

    // 1) Scores
    profile.scores.forEach(
      function (item) {
        if (item.value) {
          commit(
            'set' +
              item.vuex_type[0],
            helpers.parseStrOrNum(
              item.value
            )
          )
        }
      }
    )

    // 2) Activities
    profile.activities.forEach(
      function (item) {
        let item_value = item.value

        if (
          item.activity_type !== 2 &&
          item.activity_type !== 4 &&
          item.activity_type !== 16 &&
          item.activity_type !== 17 &&
          item.activity_type !== 9 &&
          item.activity_type !== 12
        ) {
          item_value =
            helpers.rehydrateData(
              item.value,
              item.activity_type
            )

          item_value.login = true
        }

        if (
          item.activity_type === 4
        ) {
          item_value =
            item_value.replace(
              /\\/g,
              ''
            )

          item_value =
            helpers.parseIfString(
              item_value
            )

          try {
            item_value =
              item_value.split('[')[1]

            item_value =
              item_value.slice(
                0,
                item_value.indexOf(']')
              )

            item_value =
              JSON.parse(item_value)
          } catch (error) {}
        }

        if (
          item.activity_type === 17 ||
          item.activity_type === 9 ||
          item.activity_type === 12 ||
          item.activity_type === 2
        ) {
          item_value =
            item_value.replace(
              /\\/g,
              ''
            )

          item_value =
            helpers.parseIfString(
              item_value
            )

          try {
            commit(
              'set' +
                item.vuex_type[0],
              item_value
            )

            return
          } catch (error) {
            console.log(error)
          }
        } else {
          try {
            commit(
              'set' +
                item.vuex_type[0],
              item_value
            )
          } catch (_) {}
        }
      }
    )

    // 3) Basic profile fields
    commit(
      'setPrimaryDrug',
      profile.primary_drug
    )

    commit(
      'setUserType',
      profile.user_type
    )

    // 4) Robustly parse navigation history
    //
    // Accepts:
    // - stringified JSON
    // - object
    // - nested "main"
    // - plain page map
    const raw =
      profile.navigation_history

    const parsed =
      helpers.parseIfString(raw) || {}

    let mainMap = parsed

    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.main != null
    ) {
      mainMap =
        helpers.parseIfString(
          parsed.main
        ) || {}
    }

    const navPayload = {
      main: mainMap,

      optional_history:
        helpers.parseIfString(
          parsed.optional_history
        ) || {},

      optional_completed:
        helpers.parseIfString(
          parsed.optional_completed
        ) || [],

      optional_available:
        helpers.parseIfString(
          parsed.optional_available
        ) || [],

      last_page:
        profile.progress ||
        parsed.last_page ||
        '1-0',
    }

    // 5) Hydrate navigation store
    dispatch(
      'navigation/replaceNavigationHistory',
      navPayload,
      {
        root: true,
      }
    )
  },

  setInjunctiveNorms(
    {
      commit,
      rootGetters,
    },
    injunctive_norms
  ) {
    let norms =
      injunctive_norms

    if (norms == null) {
      // Stored numeric answers, for example:
      // ["0", "4", "7"]
      const indices =
        rootGetters[
          'navigation/getPageAnswers'
        ] || []

      norms = indices.map(
        (value) => ({
          data_id:
            `injunctive-norm-${value}`,
        })
      )
    }

    // The mutation performs the server save
    commit(
      'setInjunctiveNorms',
      norms
    )
  },
}

export const getters = {
  getTenantKey(state) {
    return state.tenant_key || 'ucc'
  },

  getCampusKey(state) {
    return state.campus_key || null
  },

  getTenantConfig(state) {
    return getTenantConfigFromIndex(
      state.tenant_key || 'ucc',
      state.campus_key || null
    )
  },

  getTenantTokens(state) {
    return getTenantTokensFromIndex(
      state.tenant_key || 'ucc',
      state.campus_key || null
    )
  },

  getTenantCampuses(state) {
    return getTenantCampusesFromIndex(
      state.tenant_key || 'ucc'
    )
  },

  hasCampusPicker(state) {
    return hasCampusPickerFromIndex(
      state.tenant_key || 'ucc'
    )
  },

  getSelectedCampus(state) {
    const cfg = getTenantConfigFromIndex(
      state.tenant_key || 'ucc',
      state.campus_key || null
    )

    return cfg.selectedCampus || null
  },

  getUserType(state) {
    return state.user_type
  },

  getUserTypeName(state) {
    if (
      state.dast_score >= 9 &&
      state.dast_score <= 10
    ) {
      return 'Severe'
    } else if (
      state.dast_score >= 6 &&
      state.dast_score <= 8
    ) {
      return 'Substantial'
    } else if (
      state.dast_score >= 3 &&
      state.dast_score <= 5
    ) {
      return 'Moderate'
    } else if (
      state.dast_score >= 1 &&
      state.dast_score <= 2
    ) {
      return 'Low'
    } else if (
      state.dast_score === 0
    ) {
      return 'Non-User'
    }
  },

  getPrimaryDrug(state) {
    return state.primary_drug
  },

  getDASTScore(state) {
    return state.dast_score
  },

  getDASTAnswers(state) {
    return state.dastList
  },

  getQuizScore(state) {
    return state.quiz_score
  },

  getPosNegs(state) {
    return {
      positives:
        state.positivesList,

      negatives:
        state.negativesList,
    }
  },

  getPositives(state) {
    return state.positivesList || []
  },

  getNegatives(state) {
    return state.negativesList || []
  },

  getDescriptiveNorms(state) {
    return state.descriptiveNorms
  },

  getInjunctiveNorms(state) {
    return state.injunctiveNorms
  },

  getConfidenceToChangeScore(
    state
  ) {
    return state.confidence_score
  },

  getWillingnessToChangeScore(
    state
  ) {
    return state.willingness_score
  },

  getRelationshipScores(state) {
    return state.relationship_scores
  },

  getPersonalGoals(state) {
    return state.personalGoals
  },

  getLifeValuesScore(state) {
    return state.life_values_score
  },

  // 3-2
  getValues(state) {
    return state.values
  },

  // 3-3
  getValueScores(state) {
    const values_data_copy =
      helpers.clone(
        state.values_scores
      )

    const final = []

    state.values.forEach(
      (value, index) => {
        final.push({
          text: value,
          scores:
            values_data_copy[index]
              .scores,
        })
      }
    )

    return final
  },

  getBehaviouralAwarenessScore(
    state
  ) {
    return state.behavioural_score
  },

  getInfluences(state) {
    return state.influences
  },

  // 4-6
  getOtherInfluences(state) {
    // Also known as other reasons for use
    return state.other_influences
  },

  getNeeds(state) {
    return state.needs
  },

  getDecisionMakingScore(state) {
    return state.decision_making_score
  },

  getHRPractices(state) {
    return state.hr_practices
  },

  getHelps(state) {
    return state.helps
  },

  getSkills(state) {
    return state.skills
  },

  getReasonsForUse(state) {
    return state.reasons_for_use
  },

  getDarraghHooks(state) {
    return state.hooks
  },

  // 3-7
  getSMART(state) {
    return helpers.parseIfString(
      state.smart
    )
  },
}

export const helpers = {
  resolveTenantFromBrowser() {
    if (!process.client) {
      return 'ucc'
    }

    try {
      const host =
        window.location.hostname ||
        ''

      const subdomain =
        host.split('.')[0]
          ?.toLowerCase()
          ?.trim()

      if (
        [
          'ucc',
          'tool',
          'ul',
          'ucd',
          'atu',
          'setu',
          'tcd',
        ].includes(subdomain)
      ) {
        return normaliseTenantKey(subdomain)
      }

      const stored =
        localStorage.getItem('tenant')

      return normaliseTenantKey(stored)
    } catch (_) {
      return 'ucc'
    }
  },

  resolveCampusFromBrowser() {
    if (!process.client) {
      return null
    }
  
    try {
      const params =
        new URLSearchParams(
          window.location.search || ''
        )
  
      const fromQuery =
        params.get('campus')
  
      if (fromQuery) {
        return String(fromQuery)
          .trim()
          .toLowerCase()
      }
  
      const tenant =
        helpers.resolveTenantFromBrowser()
  
      const storageKey =
        `campus:${tenant}`
  
      const stored =
        localStorage.getItem(
          storageKey
        )
  
      if (stored) {
        return String(stored)
          .trim()
          .toLowerCase()
      }
    } catch (_) {}
  
    return null
  },

  // Rehydrate data IDs so content does not need to be stored in the backend
  rehydrateData(
    data,
    activity_type_no
  ) {
    const newdata =
      JSON.parse(data)

    const content =
      data_id_content[
        activity_type_no
      ]

    const data_ids =
      newdata.map((item) => {
        const prefix =
          data_id_content[
            activity_type_no
          ].prefix

        return prefix + item
      })

    const final_rehydrated = []

    // Recall this user's scores
    data_ids.forEach(
      function (item) {
        const found =
          content.content.find(
            (element) =>
              element.data_id === item
          )

        final_rehydrated.push(
          found
        )
      }
    )

    return final_rehydrated
  },

  clone(data) {
    return JSON.parse(
      JSON.stringify(data)
    )
  },

  parseIfString(json) {
    if (Array.isArray(json)) {
      return json
    }

    try {
      return JSON.parse(json)
    } catch (error) {
      return json
    }
  },

  parseStrOrNum(item) {
    if (typeof item === 'number') {
      return item
    }

    try {
      return JSON.parse(item)
    } catch (error) {
      return item
    }
  },

  getItem(answer, content) {
    return content.find(
      (item) =>
        item.data_id ===
        answer.data_id
    )
  },

  stripDataID(data) {
    const stripped =
      data.reduce(
        (item) => item.data_id
      )

    return stripped
  },

  async storeBlobActivityOnServer(
    item,
    type,
    $axios,
    $config
  ) {
    await $axios.$post(
      $config.BASE_API +
        '/activity/commit_activity',
      {
        value:
          JSON.stringify(item),

        type,
      }
    )
  },

  async storeActivityOnServer(
    item,
    type,
    $axios,
    $config
  ) {
    try {
      let only_ids = item.map(
        (element) =>
          element.data_id
      )

      only_ids = only_ids.filter(
        (value) => value
      )

      const value = only_ids.map(
        (item) => {
          return item.substring(
            item.lastIndexOf('-') + 1
          )
        }
      )

      await $axios.$post(
        $config.BASE_API +
          '/activity/commit_activity',
        {
          value:
            JSON.stringify(value),

          type,
        }
      )
    } catch (error) {
      // Cancelled attempt to store login parameter
    }
  },

  async storeScoreOnServer(
    value,
    type,
    $axios,
    $config
  ) {
    await $axios.$post(
      $config.BASE_API +
        '/activity/commit_score',
      {
        value: value.toString(),
        type,
      }
    )
  },

  storeStringOnServer(
    value,
    type,
    $axios,
    $config
  ) {
    $axios.$post(
      $config.BASE_API +
        '/activity/commit_activity',
      {
        value: value.toString(),
        type,
      }
    )
  },
}